import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormField as DesignFormField } from "@/components/redesign";
import { useFormDraft } from "@/hooks/useFormDraft";

type ProjectFormValues = {
  name: string;
  status: "active" | "on-track" | "delayed" | "completed";
  parcel?: string;
  lot?: string;
  flats?: number;
  shops?: number;
  area?: number;
  cost_tl?: number;
  description?: string;
  yapiDenetimFirma?: string;
  flatAreas?: number[];
  shopAreas?: number[];
};

interface ProjectFormProps {
  onSubmit: (data: ProjectFormValues) => void;
  initialData?: {
    id?: string;
    name: string;
    status: "active" | "on-track" | "delayed" | "completed";
    parcel?: string;
    lot?: string;
    flats?: number;
    shops?: number;
    area?: number;
    cost_tl?: number;
    description?: string;
    yapiDenetimFirma?: string;
    flatAreas?: number[];
    shopAreas?: number[];
  };
  submitButtonText?: string;
}

const formInputClass =
  "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md shadow-xs focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:border-brand-600";

const ProjectForm: React.FC<ProjectFormProps> = ({ onSubmit, initialData, submitButtonText }) => {
  const { t } = useTranslation();
  const [flatCount, setFlatCount] = useState<number>(initialData?.flats || 0);
  const [shopCount, setShopCount] = useState<number>(initialData?.shops || 0);

  // Form validation schema with translations
  const projectFormSchema = z.object({
    name: z.string().min(2, t('project_form.validation.min_2_chars')),
    status: z.enum(["active", "on-track", "delayed", "completed"], {
      required_error: t('project_form.validation.select_status'),
    }),
    parcel: z.string().optional(),
    lot: z.string().optional(),
    flats: z.coerce.number().int().min(0, t('project_form.validation.valid_number')).optional(),
    shops: z.coerce.number().int().min(0, t('project_form.validation.valid_number')).optional(),
    area: z.coerce.number().min(0, t('project_form.validation.valid_number')).optional(),
    cost_tl: z.coerce.number().min(0, t('project_form.validation.valid_number')).optional(),
    description: z.string().optional(),
    yapiDenetimFirma: z.string().optional(),
    // Dynamic fields for apartment and shop areas will be added to the schema
    flatAreas: z.array(z.coerce.number().min(0, t('project_form.validation.valid_area'))).optional(),
    shopAreas: z.array(z.coerce.number().min(0, t('project_form.validation.valid_area'))).optional(),
  });

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      status: initialData?.status || "active",
      parcel: initialData?.parcel || "",
      lot: initialData?.lot || "",
      flats: initialData?.flats,
      shops: initialData?.shops,
      area: initialData?.area,
      cost_tl: initialData?.cost_tl,
      description: initialData?.description || "",
      yapiDenetimFirma: initialData?.yapiDenetimFirma || "",
      flatAreas: initialData?.flatAreas || [],
      shopAreas: initialData?.shopAreas || [],
    },
  });

  // Persist in-progress project input. Scope edit drafts by record id so a draft
  // never bleeds into a different project (or into the "new project" form).
  const { clearDraft } = useFormDraft(form, {
    key: initialData?.id ? `project-edit:${initialData.id}` : "project-new",
  });

  // Update flat and shop counts when those form values change
  useEffect(() => {
    const flatsValue = form.watch("flats");
    const shopsValue = form.watch("shops");

    if (flatsValue !== undefined) {
      setFlatCount(flatsValue);
    }

    if (shopsValue !== undefined) {
      setShopCount(shopsValue);
    }
  }, [form.watch("flats"), form.watch("shops")]);

  // Generate array of flat area input fields
  const renderFlatAreaInputs = () => {
    const inputs = [];
    for (let i = 0; i < flatCount; i++) {
      inputs.push(
        <FormItem key={`flat-${i}`}>
          <DesignFormField
            label={t('project_form.area_sections.flat_number', { number: i + 1 })}
          >
            <div className="relative">
              <FormControl>
                <Input
                  className={formInputClass}
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min="0"
                  placeholder={t('project_form.area_sections.flat_area_placeholder', { number: i + 1 })}
                  {...form.register(`flatAreas.${i}`, {
                    valueAsNumber: true,
                  })}
                />
              </FormControl>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-medium text-gray-400">m²</span>
            </div>
          </DesignFormField>
          <FormMessage />
        </FormItem>
      );
    }
    return inputs;
  };

  // Generate array of shop area input fields
  const renderShopAreaInputs = () => {
    const inputs = [];
    for (let i = 0; i < shopCount; i++) {
      inputs.push(
        <FormItem key={`shop-${i}`}>
          <DesignFormField
            label={t('project_form.area_sections.shop_number', { number: i + 1 })}
          >
            <div className="relative">
              <FormControl>
                <Input
                  className={formInputClass}
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min="0"
                  placeholder={t('project_form.area_sections.shop_area_placeholder', { number: i + 1 })}
                  {...form.register(`shopAreas.${i}`, {
                    valueAsNumber: true,
                  })}
                />
              </FormControl>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-medium text-gray-400">m²</span>
            </div>
          </DesignFormField>
          <FormMessage />
        </FormItem>
      );
    }
    return inputs;
  };

  // Calculate total area from individual units
  useEffect(() => {
    const calculateTotalArea = () => {
      const flatAreas = form.watch("flatAreas") || [];
      const shopAreas = form.watch("shopAreas") || [];

      const totalFlatArea = flatAreas.reduce((sum, area) => sum + (area || 0), 0);
      const totalShopArea = shopAreas.reduce((sum, area) => sum + (area || 0), 0);

      const totalArea = totalFlatArea + totalShopArea;

      if (totalArea > 0) {
        form.setValue("area", totalArea);
      }
    };

    calculateTotalArea();
  }, [form.watch("flatAreas"), form.watch("shopAreas")]);

  const handleSubmit = (data: ProjectFormValues) => {
    onSubmit(data);
    clearDraft();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 px-4">

        {/* Basic Info Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4">
          <h2 className="text-[18px] font-bold text-brand-900">{t('project_form.sections.basic_info')}</h2>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <DesignFormField label={t('project_form.fields.project_name')} required>
                  <FormControl>
                    <Input className={formInputClass} placeholder={t('project_form.fields.project_name_placeholder')} {...field} />
                  </FormControl>
                </DesignFormField>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <DesignFormField label={t('project_form.fields.project_status')} required>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={formInputClass}>
                        <SelectValue placeholder={t('project_form.fields.status_placeholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      <SelectItem value="active">{t('project_form.statuses.active')}</SelectItem>
                      <SelectItem value="on-track">{t('project_form.statuses.on_track')}</SelectItem>
                      <SelectItem value="delayed">{t('project_form.statuses.delayed')}</SelectItem>
                      <SelectItem value="completed">{t('project_form.statuses.completed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </DesignFormField>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <DesignFormField label={t('project_form.fields.project_description')}>
                  <FormControl>
                    <Textarea
                      placeholder={t('project_form.fields.description_placeholder')}
                      className={`min-h-[100px] ${formInputClass}`}
                      {...field}
                    />
                  </FormControl>
                </DesignFormField>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Land Info Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4">
          <h2 className="text-[18px] font-bold text-brand-900">{t('project_form.sections.land_info')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="lot"
              render={({ field }) => (
                <FormItem>
                  <DesignFormField label={t('project_form.fields.lot')}>
                    <FormControl>
                      <Input className={formInputClass} placeholder={t('project_form.fields.lot_placeholder')} {...field} />
                    </FormControl>
                  </DesignFormField>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="parcel"
              render={({ field }) => (
                <FormItem>
                  <DesignFormField label={t('project_form.fields.parcel')}>
                    <FormControl>
                      <Input className={formInputClass} placeholder={t('project_form.fields.parcel_placeholder')} {...field} />
                    </FormControl>
                  </DesignFormField>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Building Info Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4">
          <h2 className="text-[18px] font-bold text-brand-900">{t('project_form.sections.building_info')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Flats — brand-400 accent */}
            <FormField
              control={form.control}
              name="flats"
              render={({ field }) => (
                <FormItem>
                  <DesignFormField label={t('project_form.fields.flat_count')}>
                    <div className="relative">
                      <FormControl>
                        <Input
                          className={`${formInputClass} border-l-2 border-l-brand-400`}
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          placeholder={t('project_form.fields.flat_count_placeholder')}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            const value = parseInt(e.target.value) || 0;
                            setFlatCount(value);
                          }}
                        />
                      </FormControl>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-brand-400 uppercase tracking-wide">daire</span>
                    </div>
                  </DesignFormField>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Shops — warning accent */}
            <FormField
              control={form.control}
              name="shops"
              render={({ field }) => (
                <FormItem>
                  <DesignFormField label={t('project_form.fields.shop_count')}>
                    <div className="relative">
                      <FormControl>
                        <Input
                          className={`${formInputClass} border-l-2 border-l-warning-300`}
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          placeholder={t('project_form.fields.shop_count_placeholder')}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            const value = parseInt(e.target.value) || 0;
                            setShopCount(value);
                          }}
                        />
                      </FormControl>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-warning-600 uppercase tracking-wide">dükkan</span>
                    </div>
                  </DesignFormField>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Area Info Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4">
          <h2 className="text-[18px] font-bold text-brand-900">{t('project_form.sections.area_info')}</h2>

          <FormField
            control={form.control}
            name="area"
            render={({ field }) => (
              <FormItem>
                <DesignFormField
                  label={t('project_form.fields.total_area')}
                  hint={flatCount > 0 || shopCount > 0 ? t('project_form.fields.total_area_auto', { defaultValue: 'Alan birimlerden otomatik hesaplanır' }) : undefined}
                >
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={formInputClass}
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="0"
                        placeholder={t('project_form.fields.total_area_placeholder')}
                        {...field}
                        disabled={flatCount > 0 || shopCount > 0}
                      />
                    </FormControl>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-medium text-gray-400">m²</span>
                  </div>
                </DesignFormField>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Dynamically render flat area inputs */}
          {flatCount > 0 && (
            <div className="space-y-3 border border-brand-100 bg-brand-25 p-4 rounded-xl">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-brand-400" />
                <h3 className="text-[15px] font-semibold text-brand-800">{t('project_form.area_sections.flat_areas')}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderFlatAreaInputs()}
              </div>
            </div>
          )}

          {/* Dynamically render shop area inputs */}
          {shopCount > 0 && (
            <div className="space-y-3 border border-warning-300 bg-warning-25 p-4 rounded-xl">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-warning-300" />
                <h3 className="text-[15px] font-semibold text-warning-600">{t('project_form.area_sections.shop_areas')}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderShopAreaInputs()}
              </div>
            </div>
          )}
        </div>

        {/* Budget Info Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4">
          <h2 className="text-[18px] font-bold text-brand-900">{t('project_form.sections.budget_info')}</h2>

          <FormField
            control={form.control}
            name="cost_tl"
            render={({ field }) => (
              <FormItem>
                <DesignFormField label={t('project_form.fields.budget_tl')}>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={formInputClass}
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="0"
                        placeholder={t('project_form.fields.budget_tl_placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-medium text-gray-400">₺</span>
                  </div>
                </DesignFormField>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Supervision Info Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4">
          <h2 className="text-[18px] font-bold text-brand-900">{t('project_form.sections.supervision_info')}</h2>

          <FormField
            control={form.control}
            name="yapiDenetimFirma"
            render={({ field }) => (
              <FormItem>
                <DesignFormField label={t('project_form.fields.supervision_company')}>
                  <FormControl>
                    <Input className={formInputClass} placeholder={t('project_form.fields.supervision_company_placeholder')} {...field} />
                  </FormControl>
                </DesignFormField>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white rounded-md h-12 text-base font-semibold"
        >
          {submitButtonText || (initialData ? t('project_form.buttons.update_project') : t('project_form.buttons.create_project'))}
        </Button>
      </form>
    </Form>
  );
};

export default ProjectForm;
